import { useEffect, useState } from 'react';

interface EconEvent {
  title: string;
  currency: string;
  date: string;
  forecast: string | null;
  previous: string | null;
  allDay?: boolean; // FRED future releases have a date but no clock time
}

/** "Tue 16:30" in the viewer's own timezone — or "Tue Jul 21" for all-day events */
function localTime(iso: string, allDay?: boolean): string {
  return new Intl.DateTimeFormat(
    'en-US',
    allDay
      ? { weekday: 'short', month: 'short', day: 'numeric' }
      : { weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }
  ).format(new Date(iso));
}

function isToday(iso: string): boolean {
  const fmt = new Intl.DateTimeFormat('en-CA');
  return fmt.format(new Date(iso)) === fmt.format(new Date());
}

export function EconCalendar() {
  const [events, setEvents] = useState<EconEvent[] | null>(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const load = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch('/api/econ-calendar', { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setEvents(data.events || []);
        setError(false);
      } catch (e) {
        console.error('Error fetching econ calendar:', e);
        setError(true);
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    const interval = setInterval(load, 3600000);
    const tick = setInterval(() => setNow(Date.now()), 60000);
    return () => {
      clearInterval(interval);
      clearInterval(tick);
    };
  }, []);

  if (error && !events) return <div className="empty-state">Calendar unavailable</div>;
  if (!events) return <div className="loading">Loading calendar...</div>;

  // Lead with today: drop events from earlier days (today's own past events stay, dimmed)
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const upcoming = events.filter((e) => new Date(e.date).getTime() >= startOfToday.getTime());

  if (upcoming.length === 0) return <div className="empty-state">No major events this week</div>;

  return (
    <div className="econ-list">
      {upcoming.map((e, i) => {
        const past = new Date(e.date).getTime() < now;
        const today = isToday(e.date);
        return (
          <div className={`econ-item${past ? ' econ-past' : ''}${today ? ' econ-today' : ''}`} key={`${e.date}-${i}`}>
            <span className="econ-time">{localTime(e.date, e.allDay)}</span>
            <span className="econ-currency">{e.currency}</span>
            <span className="econ-title">{e.title}</span>
            {(e.forecast || e.previous) && (
              <span className="econ-values">
                {e.forecast && <span title="forecast">f {e.forecast}</span>}
                {e.previous && <span title="previous">p {e.previous}</span>}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
